import React, { useState, useEffect, useRef } from "react";
import { Col, Form, Row, Table, Dropdown, Pagination } from "react-bootstrap";
import { Common, EmployeeIcons } from "../../assets/images";
import { useNavigate } from "react-router-dom";
// Redux Imports
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  setShowFilterDropdown,
  setShowExportDropdown,
  setPageSize,
  setPageNo,
  setFilterPageNo,
  setFilterPageSize,
} from "../../store/slices/iqamaSlice";
import { LoaderBlue } from "../Lotties";
import { Iqama } from "../../interfaces/iqama";
import { exportToCsv, exportToPDF } from "../../config";
import IqamaFilterModal from "../Modals/iqama-filter-modal";
import { FormProps } from "../Modals/iqama-filter-modal";
import { useReactToPrint } from "react-to-print";
import SelectSearch from "react-select-search";

const IqamaList = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const tableRef = useRef(null);
  const [print, setPrint] = useState(false);
  const handlePrint = useReactToPrint({
    content: () => tableRef.current,
    onAfterPrint: () => setPrint(false),
  });
  const handlePrinted = async () => {
    await setPrint(true);
    handlePrint();
  };
  const state = useAppSelector((state) => state.iqama);
  const profileData = useAppSelector((state) => state.settings.profileData);
  const employees = useAppSelector((state) => state.employee.allEmployeesList);
  // Options
  const options = employees.map((item) => ({
    name: item.empId + " - " + `${item.empIqamaNo}` + " - " + item.empName,
    value: item.empId,
  }));
  // Pagination && Filter
  const [filterInput, setFilterInput] = useState<FormProps>({
    status: "",
    sponsorName: "",
    professionOnIqama: "",
    fromDate: "",
    toDate: "",
    workPermitExpiry: "",
    projectId: "",
  });
  const [filterSearch, setFilterSearch] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [filteredData, setFilteredData] = useState<Iqama[]>([]);
  const totalPages = Math.ceil(state.totalRecords / state.pageSize);
  const totalCount = filterSearch
    ? state.filterTotalRecords
    : state.totalRecords;

  const filterTotalPages = Math.ceil(
    state.filterTotalRecords / state.filterPageSize,
  );

  // show 5 pages at a time in pagination bar and rest will be shown in next and previous
  const showPages = 5;
  const startPage = Math.floor((state.pageNo - 1) / showPages) * showPages;
  const endPage = startPage + showPages;
  const pages = [...Array(totalPages).keys()].slice(startPage, endPage);
  const filterStartPage =
    Math.floor((state.filterPageNo - 1) / showPages) * showPages;
  const filterEndPage = filterStartPage + showPages;
  const filterPages = [...Array(filterTotalPages).keys()].slice(
    filterStartPage,
    filterEndPage,
  );
  // handle go to page number that is after the 4 pages of current page
  const handleGoToNextPages = () => {
    //   if it is in the last 5 pages then dont do anything
    if (state.pageNo > totalPages - showPages) {
      return;
    }
    dispatch(setPageNo(state.pageNo + showPages));
  };

  const handleGoToPreviousPages = () => {
    //   if it is in the first 5 pages then dont do anything
    if (state.pageNo <= 5) {
      return;
    }
    dispatch(setPageNo(state.pageNo - showPages));
  };

  const handleFilterGoToNextPages = () => {
    //   if it is in the last 5 pages then dont do anything
    if (state.filterPageNo > filterTotalPages - showPages) {
      return;
    }
    dispatch(setFilterPageNo(state.filterPageNo + showPages));
  };

  const handleFilterGoToPreviousPages = () => {
    //   if it is in the first 5 pages then dont do anything
    if (state.filterPageNo <= 5) {
      return;
    }
    dispatch(setFilterPageNo(state.filterPageNo - showPages));
  };

  const handlePageChange = (page: any) => {
    dispatch(setPageNo(page));
  };

  const handleFilterPageChange = (page: any) => {
    dispatch(setFilterPageNo(page));
  };

  const handlePageSizeChange = (event: any) => {
    const selectedPageSize = parseInt(event.target.value, 10); // Parse the value to a number
    if (filterSearch) {
      dispatch(setFilterPageSize(selectedPageSize));
    } else {
      dispatch(setPageSize(selectedPageSize));
    }
  };
  useEffect(() => {
    setFilteredData(state.iqamaLists);
  }, [state.iqamaLists]);
  const tableData = {
    tableHeading: [
      "Sr. No.",
      "Emp Code",
      "Name",
      "Emp Sponsor Name",
      "Iqama No.",
      "Iqama Expiry",
      "Work Permit Expiry",
      "Status",
      "Actions",
    ],
    tableData: filteredData,
  };

  const csvData = tableData.tableData.map((item, index) => ({
    "Sr. No.": index + 1,
    "Emp Code": item.empId,
    "Emp Name": item.empName,
    "Emp Sponsor Name": item.empSponsorName,
    "Iqama No.": item.empIqamaNo,
    "Expiry Date": item.empIqamaExpiry
      ? new Date(item.empIqamaExpiry).toLocaleDateString()
      : "",
    "Work Permit Expiry": item.workPermitExpiry
      ? new Date(item.workPermitExpiry).toLocaleDateString()
      : "",
    "Iqama Status": item.iqamaApprovalsStatus,
  }));

  const handleReset = () => {
    setQuery("");
    setSearch("");
    setFilteredData(state.iqamaLists);
    setFilterSearch(false);
    setFilterInput({
      status: "",
      sponsorName: "",
      professionOnIqama: "",
      fromDate: "",
      toDate: "",
      workPermitExpiry: "",
      projectId: "",
    });
  };

  const twoWords = (name: string) => {
    const words = name.split(" ");
    return words[0] + " " + words[1];
  };

  return (
    <div className="user-list mt-2 section px-4 py-4">
      <IqamaFilterModal
        show={showFilterModal}
        setShow={setShowFilterModal}
        setIqama={setFilteredData}
        setFilterSearch={setFilterSearch}
        iqama={state.iqamaLists}
        filterSearch={filterSearch}
        query={query}
        filterInput={filterInput}
        setFilterInput={setFilterInput}
      />
      <Row>
        <Col sm="12">
          <div className="section-heading ">
            <h1 className="heading mb-0">Iqama List</h1>
            <div className="d-flex align-items-center justify-content-between mt-4">
              <div
                className=" d-flex align-items-center gap-2"
                style={{ width: "25%" }}
              >
                <Form.Group className="w-100" controlId="formBasicEmail">
                  <Form.Group className="position-relative input-design d-flex align-items-center">
                    <SelectSearch
                      options={options}
                      onChange={(value: any) => {
                        setQuery(value);
                        setFilterSearch(true);
                      }}
                      value={query}
                      search={true}
                      placeholder="Search Employee by Id/Iqama No/Name "
                    />
                    <img src={Common.ArrowDown} alt="message" />
                  </Form.Group>
                </Form.Group>
                {query !== "" ? (
                  <button
                    type="button"
                    className="btn primary-button w-25"
                    onClick={handleReset}
                  >
                    {" "}
                    Reset
                  </button>
                ) : filterSearch ? (
                  <button
                    type="button"
                    className="btn primary-button w-25"
                    onClick={handleReset}
                  >
                    {" "}
                    Reset
                  </button>
                ) : null}
              </div>
              <div className="d-flex align-items-center gap-4">
                <Dropdown
                  show={state.showExportDropdown}
                  className="position-relative"
                >
                  <button
                    id="dropdown-basic"
                    onClick={() => {
                      dispatch(setShowFilterDropdown(false));
                      dispatch(
                        setShowExportDropdown(!state.showExportDropdown),
                      );
                    }}
                    className=" bg-transparent dropdown-toggle-white"
                  >
                    Export{" "}
                    <img
                      src={EmployeeIcons.ArrowDownBlue}
                      className="ms-2"
                      alt="down-arrow"
                    />
                  </button>
                  <Dropdown.Menu
                    className="mt-1 custom-scrollbar"
                    show={state.showExportDropdown}
                  >
                    <Dropdown.Item
                      onClick={() => {
                        exportToCsv(csvData, "employees-list");
                        dispatch(setShowExportDropdown(false));
                      }}
                    >
                      CSV
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        dispatch(setShowExportDropdown(false));
                        handlePrinted();
                      }}
                    >
                      PDF
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                <button
                  id="dropdown-basic"
                  onClick={() => {
                    setShowFilterModal(true);
                  }}
                  className="dropdown-toggle-white dropdown-toggle-blue"
                >
                  Filter{" "}
                </button>
              </div>
            </div>
          </div>
        </Col>
        <Col sm="12">
          {state.iqamaLoading ? (
            <LoaderBlue height={70} width={70} />
          ) : profileData.permissions.showLegal ? (
            <>
              {tableData.tableData.length > 0 ? (
                <div className="p-3" ref={tableRef}>
                  {print && (
                    <>
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <img src={Common.Vision} width="110px" alt="logo" />
                        </div>
                        <div className="letter-logo text-center gap-2">
                          <img src={Common.LogoSVG} width="80px" alt="logo" />
                          <div className="mt-1">
                            <h3
                              className="logo-text mb-0"
                              style={{
                                color: "#202020",
                                fontSize: "10px",
                                fontWeight: "700",
                              }}
                            >
                              Senyar Arabian Trading Co. LTD
                            </h3>
                            <h3
                              className="logo-text logo-arabic mb-0"
                              style={{
                                color: "#202020",
                                fontSize: "13px",
                                fontWeight: "700",
                              }}
                            >
                              شركة سنيار العربية التجارية المحدودة
                            </h3>
                          </div>
                        </div>
                      </div>
                      <h1 className="sub-heading text-center mt-3">
                        Employees Iqama List
                      </h1>
                    </>
                  )}
                  <Table
                    striped
                    bordered
                    responsive
                    className={
                      print
                        ? "mt-5 text-center print-table"
                        : "mt-5 text-center"
                    }
                  >
                    <thead>
                      <tr className="border-0">
                        {tableData.tableHeading.map((item, index) => (
                          <th
                            className={
                              item === "Actions" && print
                                ? "border-0 d-none"
                                : "border-0"
                            }
                            key={index}
                          >
                            {item}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody style={{ verticalAlign: "middle" }}>
                      {tableData.tableData.map((item, index) => (
                        <tr
                          key={index}
                          style={{
                            borderColor: "rgba(32, 32, 32, 0.20)",
                            alignItems: "center",
                          }}
                        >
                          <td>
                            {totalCount > 0
                              ? filterSearch
                                ? (state.filterPageNo - 1) *
                                    state.filterPageSize +
                                  index +
                                  1
                                : (state.pageNo - 1) * state.pageSize +
                                  index +
                                  1
                              : index + 1}
                          </td>
                          <td
                            className="cursor-pointer"
                            onClick={() => {
                              navigate(`/iqama/iqama-detail/${item.empId}`, {
                                state: { data: item.empId },
                              });
                            }}
                          >
                            {item.empId}
                          </td>
                          <td
                            className="cursor-pointer"
                            onClick={() => {
                              navigate(`/iqama/iqama-detail/${item.empId}`, {
                                state: { data: item.empId },
                              });
                            }}
                          >
                            {print ? twoWords(item.empName) : item.empName}
                          </td>
                          <td>{item.empSponsorName}</td>
                          <td>{item.empIqamaNo}</td>
                          <td>
                            {item.empIqamaExpiry
                              ? new Date(
                                  item.empIqamaExpiry,
                                ).toLocaleDateString()
                              : ""}
                          </td>
                          <td>
                            {item.workPermitExpiry
                              ? new Date(
                                  item.workPermitExpiry,
                                ).toLocaleDateString() === "Invalid Date"
                                ? item.workPermitExpiry
                                : new Date(
                                    item.workPermitExpiry,
                                  ).toLocaleDateString()
                              : ""}
                          </td>
                          <td>
                            <p
                              className="mb-0 py-1 px-4 text-white mx-auto"
                              style={{
                                background:
                                  item.iqamaApprovalsStatus === "Issued" ||
                                  item.iqamaApprovalsStatus === "Valid" ||
                                  item.iqamaApprovalsStatus === "Renewd" ||
                                  item.iqamaApprovalsStatus === "Handovered"
                                    ? "rgba(57, 179, 89, 1)"
                                    : item.iqamaApprovalsStatus === "Expired"
                                      ? "rgba(235, 78, 43, 1)"
                                      : item.iqamaApprovalsStatus ===
                                            "Processing" ||
                                          item.iqamaApprovalsStatus ===
                                            "Handover Approved"
                                        ? "#29406C"
                                        : "rgba(239, 148, 0, 1)",
                                width: "fit-content",
                                borderRadius: "20px",
                              }}
                            >
                              {item.iqamaApprovalsStatus}
                            </p>
                          </td>
                          {!print && (
                            <td>
                              <button
                                className="btn p-0 border-0"
                                onClick={() => {
                                  navigate(
                                    `/iqama/iqama-detail/${item.empId}`,
                                    {
                                      state: { data: item.empId },
                                    },
                                  );
                                }}
                              >
                                <img
                                  src={Common.Eye}
                                  width="30px"
                                  height="30px"
                                  alt="edit-user"
                                />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center mt-5">
                  <h1 className="heading">No Iqama List Found</h1>
                </div>
              )}
            </>
          ) : (
            <div className="text-center mt-5">
              <h1 className="heading">Unauthorized Access</h1>
            </div>
          )}
        </Col>
        {filteredData.length > 0 && (
          <Col sm="12">
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <Form.Group
                className="d-flex align-items-center gap-2"
                controlId="formBasicEmail"
              >
                <h4
                  className="sub-heading normal-font-weight textBlack"
                  style={{ fontSize: "14px", width: "230px" }}
                >
                  Entries per page
                </h4>
                <Form.Select
                  name="pageSize"
                  onChange={handlePageSizeChange}
                  value={
                    filterSearch
                      ? state.filterPageSize.toString()
                      : state.pageSize.toString()
                  }
                  className="custom-input border-fill custom-height"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="100">100</option>
                  <option value="10000">All</option>
                </Form.Select>
              </Form.Group>
              <div className="pagination-custom">
                <Pagination className="justify-content-end">
                  <Pagination.Item
                    onClick={() =>
                      filterSearch
                        ? handleFilterGoToPreviousPages()
                        : handleGoToPreviousPages()
                    }
                  >
                    <img src={Common.DoubleArrowLeft} alt="arrow" />
                  </Pagination.Item>
                  <Pagination.Item
                    onClick={() =>
                      filterSearch
                        ? handleFilterPageChange(
                            state.filterPageNo > 1 ? state.filterPageNo - 1 : 1,
                          )
                        : handlePageChange(
                            state.pageNo > 1 ? state.pageNo - 1 : 1,
                          )
                    }
                  >
                    <img
                      src={EmployeeIcons.ArrowDownBlue}
                      className="rotate-left"
                      alt="arrow"
                    />
                  </Pagination.Item>
                  {filterSearch ? (
                    <>
                      {/* render filter pages here */}
                      {filterPages.map((page, index) => (
                        <Pagination.Item
                          key={index}
                          onClick={() => handleFilterPageChange(page + 1)}
                          className={
                            page + 1 === state.filterPageNo ? "active-page" : ""
                          }
                        >
                          {page + 1}
                        </Pagination.Item>
                      ))}
                    </>
                  ) : (
                    <>
                      {/* render pages here */}
                      {pages.map((page, index) => (
                        <Pagination.Item
                          key={index}
                          onClick={() => handlePageChange(page + 1)}
                          className={
                            page + 1 === state.pageNo ? "active-page" : ""
                          }
                        >
                          {page + 1}
                        </Pagination.Item>
                      ))}
                    </>
                  )}
                  <Pagination.Item
                    onClick={() =>
                      filterSearch
                        ? handleFilterPageChange(
                            state.filterPageNo < filterTotalPages
                              ? state.filterPageNo + 1
                              : filterTotalPages,
                          )
                        : handlePageChange(
                            state.pageNo < totalPages
                              ? state.pageNo + 1
                              : totalPages,
                          )
                    }
                  >
                    <img
                      src={EmployeeIcons.ArrowDownBlue}
                      className="rotate-right"
                      alt="arrow"
                    />
                  </Pagination.Item>
                  <Pagination.Item
                    onClick={() => {
                      filterSearch
                        ? handleFilterGoToNextPages()
                        : handleGoToNextPages();
                    }}
                  >
                    <img src={Common.DoubleArrowRight} alt="arrow" />
                  </Pagination.Item>
                </Pagination>
              </div>
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};
export default IqamaList;
